import { useEffect, useState } from 'react';
import {
  Typography as MuiTypography,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  IconButton as MuiIconButton,
  Button as MuiButton,
  Box as MuiBox,
  Autocomplete as MuiAutocomplete,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import styled from 'styled-components';
import Footer from './Footer';
import Content from './Content';
import LoadingPage from 'components/LoadingPage';
import useGetQuestionnaireResponses from 'pages/PatientRecordPage/hooks/useGetQuestionnaireResponses';
import QuestionnaireDetailsDialog from '../../QuestionnaireDetailsDialog';
import QuestionnaireAmendmentStep from '../../QuestionnaireDetailsDialog/QuestionnaireAmendmentStep';
import useGetQuestionnaireByIdLazy from 'pages/PatientRecordPage/hooks/useGetQuestionnaireByIdLazy';
import {
  endOfMonth,
  endOfToday,
  endOfWeek,
  formatISO,
  startOfToday,
  subMonths,
  subYears,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { JamIcon } from 'components/ui';
import { useDebounce } from 'use-debounce';
import AggregationButtons from './AggregationButtons';
import CalendarPopper from './CalendarPopper';
import handleChartYAxesOptions from './handleChartYAxesOptions';
import { getMainComponent } from 'lib/utils/metricComponents';
import useLoadMetricDefinitionById from 'pages/CarePlans/_service/hooks/useLoadMetricDefinitionById';

export const aggregationPeriods = ['DAILY', 'WEEKLY', 'MONTHLY'];

const getDefaultStartDateByAggregationPeriod = (aggregationPeriod) => {
  return {
    DAILY: formatISO(subMonths(startOfToday(), 1)),
    WEEKLY: formatISO(subMonths(startOfToday(), 4)),
    MONTHLY: formatISO(subYears(startOfToday(), 1)),
  }?.[aggregationPeriod];
};

export const getQuestionnaireResponseEndDate = (
  aggregationPeriod,
  selectedDate
) => {
  try {
    if (!selectedDate) return null;
    const date = new Date(selectedDate);
    return {
      DAILY: selectedDate,
      WEEKLY: formatISO(endOfWeek(date, { weekStartsOn: 1 })).split('T')[0],
      MONTHLY: formatISO(endOfMonth(date)).split('T')[0],
    }?.[aggregationPeriod];
  } catch (e) {
    console.log(e);
    return null;
  }
};

const CardItemDialog = (props) => {
  const {
    open,
    handleClose,
    metricDefinitionId,
    initialData,
    patient,
    category,
  } = props;

  // Extract main component using metadata
  const mainComponent = getMainComponent(initialData?.components);
  const {
    data: metricDefinitionData,
    loading: metricDefinitionLoading,
  } = useLoadMetricDefinitionById(metricDefinitionId, patient.id);
  const defaultAggregationFunction =
    metricDefinitionData?.metric?.metricDefinition?.metricDefinitionById
      ?.defaultAggregationFunction;

  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState(0);
  const [questionnaireDetails, setQuestionnaireDetails] = useState({});
  const [isUsingCustomDate, setIsUsingCustomDate] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [
    aggregationFunctionOverride,
    setAggregationFunctionOverride,
  ] = useState(null);

  const [filter, setFilter] = useState(() => ({
    startDateTime: formatISO(subMonths(startOfToday(), 1)),
    endDateTime: formatISO(endOfToday()),
    aggregationPeriod: 'DAILY',
  }));

  const aggregationFunction =
    aggregationFunctionOverride ?? defaultAggregationFunction ?? 'LAST_VALUE';
  const filterWithAggregation = { ...filter, aggregationFunction };

  const [debouncedFilter] = useDebounce(filterWithAggregation, 500);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const aggregationFunctionEnum = [
    { value: 'SUM', label: t('Sum') },
    { value: 'AVERAGE', label: t('Average') },
    { value: 'LAST_VALUE', label: t('Last value') },
  ];

  const {
    questionnaireAnswers,
    loading: questionnaireAnswersLoading,
    loadQuestionnaireResponsesById,
    loadQuestionnaireResponses,
  } = useGetQuestionnaireResponses({
    endDate: getQuestionnaireResponseEndDate(
      filter.aggregationPeriod,
      selected?.date
    ),
    startDate: selected?.date,
    metricDefinitionId: metricDefinitionId,
    patient: patient.id,
  });

  const handleChooseAnswer = (value) => {
    if (value) {
      setSelected(value);
    }
  };

  useEffect(() => {
    // Only load questionnaire responses for Questionnaire category
    if (category !== 'Questionnaire') return;

    if (initialData.questionnaireResponseId) {
      // initialData has questionnaireResponseId => it means the questionnaire was only answered once.
      // Fetch directly the Questionnaire response by the ID,
      // then fetch the questionnaire definition, and only show the modal when both are available.
      setLoadingModal(true);
      loadQuestionnaireResponsesById({
        variables: { id: initialData.questionnaireResponseId, patientId: patient.id },
        onCompleted: async (res) => {
          const response = res.questionnaireResponse.questionnaireResponseById;
          // Fetch the questionnaire definition for this response
          const questionnaireData = await loadQuestionnaire({
            variables: { id: response.questionnaireId, patientId: patient.id },
          });
          setQuestionnaireDetails({
            responses: response,
            questionnaire:
              questionnaireData?.data?.questionnaire?.questionnaireById,
            metricDefinitionId,
          });
          setLoadingModal(false);
          setStep(1);
        },
      });
    } else {
      if (selected) {
        loadQuestionnaireResponses();
      }
    }
  }, [initialData.questionnaireResponseId, selected]);

  const {
    questionnaire,
    loading: loadingQuestionnaire,
    loadQuestionnaire,
  } = useGetQuestionnaireByIdLazy();

  // New: handle 'See answers' click from Footer
  const onSeeAnswers = async (
    questionnaireAnswer,
    metricDefinitionId,
    isLatestVersion = false
  ) => {
    setLoadingModal(true);
    const questionnaireData = await loadQuestionnaire({
      variables: { id: questionnaireAnswer.questionnaireId, patientId: patient.id },
    });
    setQuestionnaireDetails({
      responses: questionnaireAnswer,
      questionnaire: questionnaireData?.data?.questionnaire?.questionnaireById,
      metricDefinitionId,
      isLatestVersion,
    });
    setLoadingModal(false);
    setStep(1);
  };

  useEffect(() => {
    // Questionnaire responses were fetched, fetch Questionnaire object by its ID
    // to get important data to populate Questionnaire response modal, like the FORMULA
    if (questionnaireAnswers?.[0]?.questionnaireId) {
      loadQuestionnaire({
        variables: { id: questionnaireAnswers?.[0]?.questionnaireId, patientId: patient.id },
      });
    }
  }, [questionnaireAnswers]);

  useEffect(() => {
    // Only load questionnaire responses for Questionnaire category
    if (category !== 'Questionnaire') return;

    // Questionnaire card has no value, it means the Questionnaire does not have Formula
    // There will be no values in the graph, so we should instead fetch the Questionnaire Responses of the selected date range
    if (!mainComponent?.quantityValue?.value) {
      loadQuestionnaireResponses({
        variables: {
          page: 0,
          perPage: 30,
          patient: patient.id,
          filter: {
            metricDefinitionId,
            startDate: filter.startDateTime.split('T')[0],
            endDate: filter.endDateTime.split('T')[0],
          },
        },
      });
    }
  }, [filter.startDateTime, filter.endDateTime]);

  const getContent = () => {
    if (category === 'Questionnaire') {
      return !mainComponent?.quantityValue?.value ? null : ( // If score is present, then show chart, otherwise show nothing
        <Content
          handleChooseAnswer={handleChooseAnswer}
          patient={patient}
          metricDefinitionId={initialData.metricDefinitionId}
          debouncedFilter={debouncedFilter}
          title={initialData.title}
          scoreType={mainComponent?.scoreType}
        />
      );
    }
    if (category === 'Symptom') {
      return (
        <MuiBox style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {initialData?.attributes?.map((attr, idx) => {
            return (
              <MuiBox>
                <Typography
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  {t(attr.title)}
                </Typography>
                <Content
                  yAxesOptions={handleChartYAxesOptions(attr)}
                  handleChooseAnswer={handleChooseAnswer}
                  patient={patient}
                  metricDefinitionId={attr.metric.metricDefinitionId}
                  debouncedFilter={debouncedFilter}
                  title={attr.title}
                  scoreType={mainComponent?.scoreType}
                />
              </MuiBox>
            );
          })}
        </MuiBox>
      );
    }
    return (
      <Content
        handleChooseAnswer={handleChooseAnswer}
        patient={patient}
        metricDefinitionId={metricDefinitionId}
        debouncedFilter={debouncedFilter}
        title={initialData?.title}
        scoreType={mainComponent?.scoreType}
      />
    );
  };

  const returnStepContent = () => {
    switch (step) {
      default:
        return (
          <>
            <SMuiDialogTitle>
              <MuiTypography variant="h4" align="center">
                {initialData?.title}
              </MuiTypography>
              <MuiIconButton
                color="primary"
                size="small"
                aria-label="close"
                classes={{ root: 'closeButton' }}
                onClick={(e) => handleClose(e)}
                data-id="close-button"
              >
                <CloseIcon classes={{ root: 'icon' }} />
              </MuiIconButton>
            </SMuiDialogTitle>
            <SMuiDialogContent>
              {metricDefinitionLoading ? (
                <LoadingPage style={{ height: 400 }} />
              ) : (
                <SMuiContentWrapper>
                  <MuiBox
                    style={{
                      height: 40,
                      marginTop: 20,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                  >
                    <MuiBox style={{ height: 32, display: 'flex', gap: 10 }}>
                      <MuiButton
                        variant="outlined"
                        style={{
                          display: 'flex',
                          gap: 4,
                          padding: '6px 12px',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCalendarOpen(!isCalendarOpen);
                        }}
                      >
                        <JamIcon
                          size={20}
                          icon="calendar"
                          color={theme.palette.colors.blue100}
                        />
                        {t('Custom')}
                      </MuiButton>

                      {mainComponent?.quantityValue?.value ||
                      category !== 'Questionnaire' ? (
                        <AggregationButtons
                          filter={filter}
                          setFilter={setFilter}
                          setSelected={setSelected}
                          isUsingCustomDate={isUsingCustomDate}
                          getDefaultStartDateByAggregationPeriod={
                            getDefaultStartDateByAggregationPeriod
                          }
                        />
                      ) : null}
                    </MuiBox>
                    {mainComponent?.quantityValue?.value ||
                    category === 'Metric' ? (
                      <SMuiAutocomplete
                        options={['SUM', 'AVERAGE', 'LAST_VALUE']}
                        getOptionLabel={(o) =>
                          aggregationFunctionEnum.find((i) => o === i.value)
                            ?.label
                        }
                        getOptionValue={(o) => o}
                        value={aggregationFunction}
                        disableClearable
                        onChange={(_, value) => {
                          setAggregationFunctionOverride(value);
                        }}
                        renderInput={(params) => {
                          return (
                            <TextField
                              {...params}
                              label={t('Aggregation function')}
                              style={{
                                width: 160,
                                height: 40,
                                marginRight: 50,
                              }}
                            />
                          );
                        }}
                      />
                    ) : null}
                    {isCalendarOpen && (
                      <CalendarPopper
                        setIsCalendarOpen={setIsCalendarOpen}
                        filter={filter}
                        setFilter={setFilter}
                        setIsUsingCustomDate={setIsUsingCustomDate}
                        setSelected={setSelected}
                      />
                    )}
                  </MuiBox>

                  {getContent()}
                  {(selected || !mainComponent?.quantityValue?.value) && (
                    <Footer
                      selected={{
                        ...selected,
                        title: initialData?.title,
                        ...filterWithAggregation,
                      }}
                      questionnaireAnswers={questionnaireAnswers}
                      loading={
                        questionnaireAnswersLoading || loadingQuestionnaire
                      }
                      isQuestionnaire={category === 'Questionnaire'}
                      metricDefinitionId={metricDefinitionId}
                      onSeeAnswers={onSeeAnswers}
                      patientId={patient.id}
                    />
                  )}
                </SMuiContentWrapper>
              )}
            </SMuiDialogContent>
          </>
        );

      case 1:
        if (loadingModal) {
          return <LoadingPage style={{ height: 492 }} />;
        }
        if (!questionnaire) {
          console.warn(
            '[DEBUG][CardItemDialog] questionnaire is missing or undefined!'
          );
        }
        return (
          <QuestionnaireDetailsDialog
            back={() => {
              if (initialData?.questionnaireResponseId) {
                handleClose();
              } else {
                setStep(0);
              }
            }}
            close={(e) => handleClose(e)}
            initialValues={{ ...questionnaireDetails, questionnaire }}
            update={() => setStep(2)}
            patientId={patient.id}
          />
        );

      case 2:
        if (loadingModal) {
          return <LoadingPage style={{ height: 492 }} />;
        }
        return (
          <QuestionnaireAmendmentStep
            questionnaire={questionnaire}
            responses={questionnaireDetails.responses}
            metricDefinitionId={metricDefinitionId}
            onCancel={() => setStep(1)}
            onSubmitSuccess={() => {
              // Refresh questionnaire responses
              loadQuestionnaireResponses();
              setStep(0);
            }}
          />
        );
    }
  };

  return (
    <SMuiDialog open={open} onClose={(e) => handleClose(e)}>
      {returnStepContent()}
    </SMuiDialog>
  );
};

const SMuiAutocomplete = styled(MuiAutocomplete)`
  && {
    height: 40px;
    .MuiOutlinedInput-root {
      height: 40px;
      padding: 0 0 0 5px;
    }
    input {
      padding: 0 0 0 5px !important;
    }
  }
`;

const SMuiContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SMuiDialog = styled(MuiDialog)`
  .MuiDialog-paper {
    min-width: 65.6vw;
    max-width: fit-content;
    min-height: 600px;
    max-height: calc(100vh - 40px);
  }
`;

const SMuiDialogContent = styled(MuiDialogContent)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SMuiDialogTitle = styled(MuiDialogTitle)`
  width: 100%;
  text-align: center;
  padding: 28px 60px 0;
  min-width: 350px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h4 {
    text-align: center;
    width: 100%;
  }

  .closeButton {
    margin-left: auto;
    font-size: 10px;
    color: ${(props) => props.theme.palette.grey[900]};
    border: 1px solid ${(props) => props.theme.palette.grey[200]};
    border-radius: 10px;
    height: 32px;
    width: 32px;
  }
  .icon {
    font-size: 16px;
  }
`;

export default CardItemDialog;
